import { getOfferMarkerInformation } from 'api/offer';
import {
  DEFAULT_LATITUDE,
  DEFAULT_LONGITUDE,
  DEFAULT_ZOOM,
} from 'constants/map';
import {
  FunctionComponent,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setModal, setNormalMarkerInfo } from 'redux/store/mapReducer';
import { RootState } from 'redux/store/rootReducer';
import styled from 'styled-components';
import { OfferFilter, OfferMapFilter, OfferMarker } from 'types/offer';
import { getReactNativeMessage } from 'utils/ReactNativeMessage';
import { convertDate } from 'utils/date';
import { TMapOptions } from '../../types/naverMap';
import NormalMapMarker from './NormalMapMarker';

const NormalMap: FunctionComponent<TMapOptions> = () => {
  const map = useRef<naver.maps.Map>();
  const dispatch = useDispatch();
  const { bldInfo } = useSelector((state: RootState) => state.mapStore);
  const [isLoading, setIsLoading] = useState(false);
  const [markerArray, setMarkerArray] = useState<OfferMarker[]>([]);
  const [selectedBldNm, setSelectedBldNm] = useState<string>('');
  const [filteredData, setFilteredData] = useState<OfferFilter>({
    area: [0, 80],
    year: 'all',
    date: ['01', '12'],
    deposit: [0, 16],
    managementCost: [0, 40],
    monthlyDeposit: [0, 8],
    monthlyPrice: [0, 200],
    price: [0, 24],
    tradeType: ['매매', '월세', '전세'],
  });

  useLayoutEffect(() => {
    getReactNativeMessage({ handleFilteredData });

    drawMap();
  }, []);

  useEffect(() => {
    if (map.current) moveToSelectedBuildingPosition(map.current);
  }, [bldInfo]);

  useEffect(() => {
    if (!map.current) return () => {};

    drawMarker(map.current, filteredData);
    const idleListener = map.current.addListener('idle', () => {
      if (map.current) {
        saveCurrentPositionToLocalStorage(map.current);
        drawMarker(map.current, filteredData);
        setSelectedBldNm('');
      }
    });

    const clickListener = map.current.addListener('click', () => {
      setSelectedBldNm('');
      setNormalMarkerInfo({});
      dispatch(setModal(false));
    });
    return () => {
      naver.maps.Event.removeListener(idleListener);
      naver.maps.Event.removeListener(clickListener);
    };
  }, [filteredData]);

  const moveToSelectedBuildingPosition = (map: naver.maps.Map) => {
    if (bldInfo) {
      const newCenter = new naver.maps.LatLng(
        bldInfo?.complexLatCenter,
        bldInfo?.complexLonCenter,
      );

      map.morph(newCenter, 17);
    }
  };

  const getSavedPositionFromLocalStorage = () => {
    const zoomLevel = Number(window.localStorage.getItem('normalZoom'));
    const centerY = Number(window.localStorage.getItem('normalCenterY'));
    const centerX = Number(window.localStorage.getItem('normalCenterX'));

    return { zoomLevel, centerY, centerX };
  };

  const saveCurrentPositionToLocalStorage = (map: naver.maps.Map) => {
    const { x, y } = map.getCenter();
    const zoom = map.getZoom();

    window.localStorage.setItem('normalZoom', `${zoom}`);
    window.localStorage.setItem('normalCenterY', `${y}`);
    window.localStorage.setItem('normalCenterX', `${x}`);
  };

  async function drawMap() {
    const { zoomLevel, centerX, centerY } = getSavedPositionFromLocalStorage();
    const mapOptions: naver.maps.MapOptions = {
      zoom: zoomLevel || DEFAULT_ZOOM,
      center: new naver.maps.LatLng(
        centerY || DEFAULT_LATITUDE,
        centerX || DEFAULT_LONGITUDE,
      ),
    };

    map.current = new naver.maps.Map('map', mapOptions);
    map.current?.morph(
      new naver.maps.LatLng(
        centerY || DEFAULT_LATITUDE,
        centerX || DEFAULT_LONGITUDE,
      ),
      zoomLevel || DEFAULT_ZOOM,
    );
  }

  const drawMarker = async (map: naver.maps.Map, filter: OfferFilter) => {
    saveCurrentPositionToLocalStorage(map);

    const zoom = map.getZoom();
    const { x: longitude, y: latitude } = map.getCenter();
    const bounds = map.getBounds();
    const { x: west, y: south } = bounds.getMin();
    const { x: east, y: north } = bounds.getMax();
    const mapData: OfferMapFilter = {
      zoom,
      latitude,
      longitude,
      west,
      north,
      east,
      south,
      areaMin: filter.area[0],
      areaMax: filter.area[1],
      availableYear: filter.year === '전체' ? 'all' : filter?.year,
      dateMin: convertDate(filter.date[0]),
      dateMax: convertDate(filter.date[1]),
      depositMin: filter.deposit[0] * 10000,
      depositMax: filter.deposit[1] * 10000,
      managementCostMin: filter.managementCost[0] * 10000,
      managementCostMax: filter.managementCost[1] * 10000,
      monthlyDepositMin: filter.monthlyDeposit[0] * 10000,
      monthlyDepositMax: filter.monthlyDeposit[1] * 10000,
      monthlyPriceMin: filter.monthlyPrice[0],
      monthlyPriceMax: filter.monthlyPrice[1],
      priceMin: filter.price[0] * 10000,
      priceMax: filter.price[1] * 10000,
      tradeType: filter.tradeType,
    };

    try {
      setIsLoading(true);

      const result = await getOfferMarkerInformation(mapData);
      if (result.error) {
        return;
      }
      if (result.data) {
        setMarkerArray(result.data);
      }
      dispatch(setModal(false));
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilteredData = (item: OfferFilter) => {
    setFilteredData(item);
  };

  const handleClickMarker = (bldNm: string) => {
    setSelectedBldNm(bldNm);
  };

  return (
    <>
      <NaverMap id="map">
        {!isLoading && !markerArray.length && (
          <PopUp>
            <PopupLabel>검색 결과가 없습니다.</PopupLabel>
            <PopupLabel>필터를 변경하거나</PopupLabel>
            <PopupLabel>지도를 이동시켜 주세요.</PopupLabel>
          </PopUp>
        )}

        {markerArray?.map((markerInfo) => {
          return (
            <NormalMapMarker
              key={
                markerInfo.center.latitude +
                markerInfo.center.longitude +
                markerInfo.complexId
              }
              map={map.current}
              markerInfo={markerInfo}
              onClickMarker={handleClickMarker}
              selectedBldNm={selectedBldNm}
            />
          );
        })}
      </NaverMap>
    </>
  );
};

export default NormalMap;

const PopUp = styled.div`
  border-radius: 8px;
  z-index: 1;
  top: 20%;
  left: 50%;
  transform: translateX(-50%) translateY(-50%);
  padding: 16px;
  opacity: 0.5;
  position: absolute;
  background-color: black;
`;

const PopupLabel = styled.div`
  font-family: Pretendard;
  font-style: normal;
  font-weight: bold;
  font-size: 13px;
  line-height: 20px;
  vertical-align: middle;
  text-align: center;
  color: white;
`;

const NaverMap = styled.div`
  width: 100%;
  height: 100%;
`;

NormalMap.defaultProps = {
  zoom: 10,
  center: { x: 33.3572421, y: 126.5322317 },
  mapTypeId: 'NORMAL',
};
